import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';

// pdfjs-dist é pesado (~1.5MB). Dynamic import mantém o lib fora do bundle
// principal — só baixa quando o componente é realmente usado. Sem isso o
// `ng serve`/`ng build` estoura memória durante o sealing do webpack.
type PdfJsModule = typeof import('pdfjs-dist');

let pdfjsLoader: Promise<PdfJsModule> | null = null;
function loadPdfJs(): Promise<PdfJsModule> {
  if (!pdfjsLoader) {
    pdfjsLoader = import('pdfjs-dist').then(mod => {
      // Worker servido como asset estático (configurado no angular.json).
      // Sem isso o pdfjs cai num CDN remoto e quebra com CSP/offline.
      (mod as any).GlobalWorkerOptions.workerSrc = 'assets/pdfjs/pdf.worker.min.js';
      return mod;
    });
  }
  return pdfjsLoader;
}

// Marcador sobre o PDF. x/y são NORMALIZADOS (0..1) em relação à página,
// então independem da escala de renderização e do zoom do browser.
export interface PdfMarker {
  page: number; // 1-based
  x: number;    // 0..1
  y: number;    // 0..1
  svg: string;
  label?: string; // rótulo (ex.: 'A' / 'B') exibido na diagonal abaixo do ícone
  id?: string;  // referência opcional (ex.: id do registro de arquivo-mestre)
  active?: boolean; // ponto vigente para posicionamento (destaque tracejado)
}

// Linha (tracejada) ligando dois pontos de uma mesma sinalização.
export interface PdfLine {
  page: number;
  x1: number; y1: number; // 0..1
  x2: number; y2: number; // 0..1
}

@Component({
  selector: 'app-pdf-viewer',
  template: `
    <div class="pdf-viewer-pages" #container
         [class.panning]="isPanning"
         (mousedown)="onPanStart($event)"
         (mousemove)="onPanMove($event)"
         (mouseup)="onPanEnd()"
         (mouseleave)="onPanEnd()">
      <div class="pdf-viewer-page-wrap" *ngFor="let _ of pages; let i = index"
           [class.editable]="editable"
           (click)="onPageClick($event, i)">
        <canvas class="pdf-viewer-page" [attr.data-page]="i + 1"></canvas>
        <svg class="pdf-line-layer" *ngIf="linesForPage(i + 1).length">
          <line *ngFor="let l of linesForPage(i + 1)"
                [attr.x1]="pct(l.x1)" [attr.y1]="pct(l.y1)"
                [attr.x2]="pct(l.x2)" [attr.y2]="pct(l.y2)"></line>
        </svg>
        <ng-container *ngFor="let m of markers">
          <span *ngIf="m.page === i + 1"
               class="pdf-marker-wrap"
               [class.clickable]="markersClickable"
               [class.active]="m.active"
               [style.left.%]="m.x * 100"
               [style.top.%]="m.y * 100"
               (click)="onMarkerClick($event, m)">
            <img class="pdf-marker-icon" [src]="m.svg">
            <span class="pdf-marker-label" *ngIf="m.label">{{ m.label }}</span>
          </span>
        </ng-container>
      </div>
      <div *ngIf="error" class="pdf-viewer-error">Não foi possível renderizar o PDF.</div>
    </div>
    <div *ngIf="loading" class="pdf-viewer-loading">
      <div class="pdf-viewer-spinner"></div>
      <span>Carregando arquivo…</span>
    </div>
  `,
  styles: [`
    :host { display: block; position: relative; width: 100%; height: 100%; overflow: auto; background: #525659; }
    .pdf-viewer-loading { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: #fff; font-size: 14px; background: #525659; z-index: 10; }
    .pdf-viewer-spinner { width: 40px; height: 40px; border: 3px solid rgba(255,255,255,0.25); border-top-color: #fff; border-radius: 50%; animation: pdf-viewer-spin 0.8s linear infinite; }
    @keyframes pdf-viewer-spin { to { transform: rotate(360deg); } }
    .pdf-viewer-pages { display: flex; flex-direction: column; align-items: center; padding: 8px 0; gap: 8px; cursor: grab; }
    .pdf-viewer-pages.panning { cursor: grabbing; }
    .pdf-viewer-page-wrap { position: relative; display: block; width: fit-content; max-width: 100%; box-shadow: 0 2px 6px rgba(0,0,0,0.3); }
    .pdf-viewer-page-wrap.editable { cursor: crosshair; }
    canvas.pdf-viewer-page { display: block; max-width: 100%; height: auto; background: #fff; }
    .pdf-line-layer { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
    .pdf-line-layer line { stroke: #37474f; stroke-width: 2; stroke-dasharray: 6 4; }
    .pdf-marker-wrap { position: absolute; transform: translate(-50%, -50%); pointer-events: auto; line-height: 0; }
    .pdf-marker-wrap.clickable { cursor: pointer; }
    .pdf-marker-wrap.active::before { content: ''; position: absolute; top: 50%; left: 50%; width: 46px; height: 46px; transform: translate(-50%, -50%); box-sizing: border-box; border: 2px dashed #268fff; border-radius: 50%; background: rgba(38,143,255,0.08); animation: pdf-marker-spin 6s linear infinite; }
    @keyframes pdf-marker-spin { to { transform: translate(-50%, -50%) rotate(360deg); } }
    .pdf-marker-icon { width: 32px; height: 32px; display: block; }
    .pdf-marker-label { position: absolute; left: 78%; top: 72%; font: bold 12px Arial, sans-serif; color: #263238; background: rgba(255,255,255,0.9); border-radius: 50%; width: 16px; height: 16px; line-height: 16px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.4); }
    .pdf-viewer-error { color: #fff; padding: 16px; font-size: 14px; }
  `],
})
export class PdfViewerComponent implements OnChanges, OnDestroy {
  @Input() src: string | null = null;
  @Input() markers: PdfMarker[] = [];
  @Input() lines: PdfLine[] = [];
  @Input() editable = false;
  @Input() markersClickable = false;
  @Output() pageClick = new EventEmitter<{ page: number; x: number; y: number }>();
  @Output() markerClick = new EventEmitter<{ id?: string }>();
  @ViewChild('container', { static: false }) container?: ElementRef<HTMLDivElement>;

  pages: unknown[] = [];
  error = false;
  loading = false;
  isPanning = false;

  // Pan por arraste: manipula o scroll do host (que tem overflow:auto).
  private _panStartX = 0;
  private _panStartY = 0;
  private _panScrollLeft = 0;
  private _panScrollTop = 0;
  private _panned = false; // usado pra suprimir o click após arrastar

  private loadingTask: { destroy: () => void } | null = null;

  constructor(private _host: ElementRef<HTMLElement>) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    // Só re-renderiza os canvases quando o src muda. Alterações em markers/lines/
    // editable são refletidas pelo template sem repintar o PDF.
    if (changes['src']) await this.render();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  pct(v: number): string {
    return `${v * 100}%`;
  }

  linesForPage(page: number): PdfLine[] {
    return (this.lines || []).filter(l => l.page === page);
  }

  // Clique numa página em modo editável → emite coordenada normalizada.
  // Suprime o clique quando veio de um arraste (pan).
  onPageClick(event: MouseEvent, pageIndex: number) {
    if (this._panned) { this._panned = false; return; }
    if (!this.editable) return;
    const wrap = event.currentTarget as HTMLElement;
    const canvas = wrap.querySelector('canvas.pdf-viewer-page') as HTMLCanvasElement | null;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    this.pageClick.emit({ page: pageIndex + 1, x, y });
  }

  onMarkerClick(event: MouseEvent, marker: PdfMarker) {
    if (this._panned) { this._panned = false; return; }
    event.stopPropagation();
    this.markerClick.emit({ id: marker.id });
  }

  onPanStart(event: MouseEvent) {
    if (event.button !== 0) return;
    this.isPanning = true;
    this._panned = false;
    this._panStartX = event.clientX;
    this._panStartY = event.clientY;
    const host = this._host.nativeElement;
    this._panScrollLeft = host.scrollLeft;
    this._panScrollTop = host.scrollTop;
  }

  onPanMove(event: MouseEvent) {
    if (!this.isPanning) return;
    const dx = event.clientX - this._panStartX;
    const dy = event.clientY - this._panStartY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this._panned = true;
    const host = this._host.nativeElement;
    host.scrollLeft = this._panScrollLeft - dx;
    host.scrollTop = this._panScrollTop - dy;
  }

  onPanEnd() {
    this.isPanning = false;
  }

  private cleanup() {
    if (this.loadingTask) {
      this.loadingTask.destroy();
      this.loadingTask = null;
    }
  }

  private async render() {
    this.cleanup();
    this.error = false;
    this.pages = [];
    if (!this.src) return;

    // Mostra o loading até as páginas serem pintadas nos canvases (evita o
    // "cinza" enquanto o pdfjs baixa e renderiza o documento).
    this.loading = true;
    try {
      const pdfjsLib = await loadPdfJs();
      const task = (pdfjsLib as any).getDocument({ url: this.src, withCredentials: false });
      this.loadingTask = task;
      const pdf = await task.promise;
      this.pages = Array.from({ length: pdf.numPages });

      // Espera o Angular renderizar os <canvas> via *ngFor antes de pintar.
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      const canvases = this.container?.nativeElement.querySelectorAll<HTMLCanvasElement>('canvas.pdf-viewer-page');
      if (!canvases) return;
      for (let i = 0; i < pdf.numPages; i++) {
        const page = await pdf.getPage(i + 1);
        // Scale 1.5 dá renderização nítida sem inflar memória pra PDFs grandes.
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = canvases[i];
        if (!canvas) continue;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: ctx, viewport }).promise;
      }
    } catch (err) {
      console.error('[PdfViewer] erro ao renderizar PDF', err);
      this.error = true;
    } finally {
      this.loading = false;
    }
  }
}
